import { Injectable } from '@angular/core';
import { saveAs } from 'file-saver/dist/FileSaver';
import { ApiService } from '../common/services/api.service';
import { MessageService } from '../common/services/message.service';
import { LocalStorageService } from '../common/services/localStorage.service';
import { LEAD_SOURCE } from './collection-infrastructure.enums';
import { RNDataTransferService } from '../common/services/rn-data-transfer.service';
import { resolveHostedUrl } from 'src/app/constants/hosted-api-urls';
import { UtilService } from '../util/util.service';

@Injectable()
export class CollectionInfrastructureService {
  private isDownloading: boolean = false;
  constructor(
    private _api: ApiService,
    private _message: MessageService,
    private _localStorage: LocalStorageService,
    private _rnDataTransferService: RNDataTransferService,
    private _util: UtilService
  ) {}

  async getAvailablePlans(restaurantId: string): Promise<any> {
    try {
      const response = await this._api
        .subscriptionPlans(restaurantId, this._localStorage.get('planType'))
        .toPromise();
      // Handle response as needed
      return Promise.resolve(response);
    } catch (error) {
      console.error(error);
      return Promise.reject(error);
    }
  }
  async triggerPromiseToPayLater(restaurantId: string): Promise<boolean> {
    const payload = {
      promiseState: 'complete',
    };
    try {
      // Check if the restaurant has an active subscription plan
      // If there's a subscription plan, trigger promise to pay later
      const response = await this._api
        .updatePromiseState(payload, restaurantId)
        .toPromise();
      const data = (response || {}).data || {};
      if (data.status === 1) {
        this._message.showSuccess(data.message);
        return true;
      } else {
        this._message.showError(data.message);
        return false;
      }
    } catch (error) {
      console.error(error);
      this._message.showError(
        'An error occurred while triggering promise to pay later'
      );
      return false;
    }
  }
  async getRestaurantDetails(restaurantId: string): Promise<any> {
    let payload = {
      rest_id: restaurantId,
      fields: {
        currentPlan: 1,
        blockFeatureList: 1,
        blockedDuesPending: 1,
      },
    };
    try {
      const response = await this._api
        .fetchRestaurantDetailsWithPayload(payload)
        .toPromise();
      return Promise.resolve(response);
    } catch (error) {
      console.error(error);
      return Promise.reject(error);
    }
  }
  async dismissBannerSubscription(restaurantId: string): Promise<boolean> {
    try {
      const response = await this._api
        .dismissBannerSubscription(restaurantId)
        .toPromise();
      return (response || {}).status === 1;
    } catch (error) {
      console.error(error);
      this._message.showError((error || {}).message);
      return false;
    }
  }
  async downloadNegativeCurrentBalanceReport(
    hqId: string,
    restaurantId?: string
  ): Promise<boolean> {
    try {
      const response = await this._api
        .downloadNegativeCurrentBalanceReport(hqId, restaurantId || '')
        .toPromise();
      // Handle response as needed
      return Promise.resolve(response);
    } catch (error) {
      console.error(error);
      return Promise.reject(error);
    }
  }
  async getPaymentMethodsForCollection(
    restaurantId: string,
    client_id: string
  ): Promise<boolean> {
    try {
      const response = await this._api
        .getPaymentMethodsForCollection(restaurantId, client_id)
        .toPromise();
      // Handle response as needed
      return Promise.resolve(response);
    } catch (error) {
      this._message.showError((error || {}).message);
      console.error(error);
      return Promise.reject(error);
    }
  }
  async createSettlementBill(payload: {
    restaurant_id: string;
    amount: number;
    country_code: string;
    payment_gateway: string;
    is_hq: boolean;
    recurring_status: boolean;
    payment_method?: string;
    payment_description?: string;
    client_id?: string;
  }): Promise<string | null> {
    try {
      const response: any = await this._api
        .createSettlementBill({
          restaurant_data: [
            {
              restaurant_id: payload.restaurant_id,
              amount: payload.amount,
            },
          ],
          country_code: payload.country_code,
          payment_gateway: payload.payment_gateway,
          payment_description:
            payload.payment_description || 'Negative Balance payment',
          client_id: payload.client_id || 'negative_balance_payment',
          is_hq: payload.is_hq,
          recurring_status: payload.recurring_status,
          payment_method: payload.payment_method || 'online',
        })
        .toPromise();
      return (
        (((response || {}).data || {}).data || {}).bill_id ||
        ((response || {}).data || {}).bill_id ||
        null
      );
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async getRestaurantPaymentMethods(countryCode: string): Promise<any> {
    try {
      const response: any = await this._api
        .getRestaurantPaymentMethods(countryCode)
        .toPromise();
      return (response || {}).data || {};
    } catch (error) {
      console.error(error);
      return Promise.reject(error);
    }
  }

  async getPaymentStatus(payload: { payment_id: string }): Promise<boolean> {
    try {
      const response = await this._api.getPaymentStatus(payload).toPromise();
      return Promise.resolve(response);
    } catch (error) {
      console.error(error);
      return Promise.reject(error);
    }
  }
  async getInvoices(
    restaurantIds?: string,
    category?: string,
    startDate?: string,
    endDate?: string
  ): Promise<any> {
    try {
      const response = await this._api
        .getInvoices(
          restaurantIds || '',
          category || '',
          startDate || '',
          endDate || ''
        )
        .toPromise();
      return Promise.resolve((response.data || {}).data);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      return Promise.reject(error);
    }
  }

  async upgrade(): Promise<any> {
    try {
      const payload = {
        firstname: this._localStorage.get('restaurant_name'),
        phone: this._localStorage.get('restaurant_phone'),
        email: this._localStorage.get('email'),
        source: 'selfonboarding',
        restaurant_name: this._localStorage.get('restaurant_name'),
        website: 'https://onboarding.easyeat.ai/',
        lead_source: LEAD_SOURCE[this._localStorage.get('country')],
      };
      const response: any = await this._api
        .createHubspotContact(payload)
        .toPromise();
      return Promise.resolve(response);
    } catch (error) {
      console.error(error);
      return Promise.reject(error);
    }
  }

  async dismissBanner(restaurantId: string, isHq?: boolean): Promise<boolean> {
    try {
      let api = isHq
        ? { apiKey: 'dismissBannerHq', id: this._localStorage.get('hq_id') }
        : { apiKey: 'dismissBanner', id: restaurantId };
      const response = await this._api[api.apiKey](api.id).toPromise();
      return (response || {}).status === 1;
    } catch (error) {
      console.error(error);
      this._message.showError((error || {}).message);
      return false;
    }
  }

  async getBannerDetails(restaurantId: string): Promise<any> {
    try {
      const response = await this._api.getBanner(restaurantId).toPromise();
      return {
        data: (response || {}).data || {},
        status: (response || {}).status || 0,
      };
    } catch (error) {
      console.error(error);
      return { data: {}, status: 0 };
    }
  }

  async getHqBalance(): Promise<{
    data: any[];
    bannerDismissed: boolean;
    status: number;
  }> {
    try {
      const response = await this._api
        .getHqBalance(this._localStorage.get('hq_id'))
        .toPromise();
      if ((response || {}).status === 1) {
        const status = (response || {}).status;
        const data = this.convertArrayData((response || {}).data);
        const bannerDismissed =
          ((response || {}).meta_data || {}).bannerDismissed || false;
        return { data, bannerDismissed, status };
      }
      return { data: [], bannerDismissed: false, status: 0 };
    } catch (error) {
      console.error(error);
      return { data: [], bannerDismissed: false, status: 0 };
    }
  }

  convertArrayData(dataArray: any[]): any[] {
    const convertedArray: any[] = [];
    dataArray.forEach((data) => {
      const convertedData = {
        name: (data || {}).restaurantName,
        amount: (data || {}).finalBill,
        amount_text: (data || {}).finalBill_text,
        status: 'pending',
        rest_id: (data || {}).restaurantId,
      };
      convertedArray.push(convertedData);
    });
    return convertedArray;
  }

  async download(restaurantId: string): Promise<any> {
    if (this.isDownloading) {
      return Promise.resolve(); // If already downloading, return a resolved Promise
    }

    this.isDownloading = true; // Set the flag to true before starting the download

    try {
      const response = await this._api
        .downloadBalanceBill(restaurantId)
        .toPromise();
      if ((response || {}).data) {
        if (this._util.isReactDevice()) {
          const eventData = {
            endpoint: `${resolveHostedUrl('nestURL')}/api/negative-balance/download/${restaurantId}`,
          };
          this._rnDataTransferService.sendDownloadCustomReportActionToRN(
            eventData
          );
        } else {
          saveAs((response || {}).data, 'EasyEat_bill.csv');
        }
      } else {
        this._message.showError((response || {}).message);
      }
      return Promise.resolve(response); // Return the API response
    } catch (error) {
      console.error(error);
      this._message.showError(error.msg);
      // Implement error handling here (e.g., show a user-friendly error message)
      return Promise.reject(error); // Return a rejected Promise with the error
    } finally {
      this.isDownloading = false; // Set the flag back to false after download is completed or encountered an error
    }
  }

  async patchForm(restaurantId: string, type: string): Promise<any> {
    try {
      const response = await this._api
        .talkToexpert(restaurantId, type)
        .toPromise();
      if ((response || {}).status === 1) {
        const restData = (response || {}).data;
        const formValue = {
          restaurantName: restData.restaurantName,
          picName: restData.picName,
          mobile: restData.picPhoneNumber,
          email: restData.picEmail,
        };
        return { formValue, restData }; // Return both form data and rest data
      }
      return {};
    } catch (error) {
      console.error(error);
      // Implement error handling here (e.g., show a user-friendly error message)
      return {};
    }
  }
  async submitToExpert(
    restaurantId: string,
    payloadForm: any
  ): Promise<boolean> {
    try {
      const payload = payloadForm;
      const response = await this._api
        .genrateTicketExpert(payload, restaurantId)
        .toPromise();
      (response || {}).status === 1
        ? this._message.showSuccess('Form Submitted SuccessFully')
        : this._message.showError((response || {}).message);
      return (response || {}).status === 1;
    } catch (error) {
      console.error(error);
      // Implement error handling here (e.g., show a user-friendly error message)
      return false;
    }
  }
  async pledge(restaurantId: string): Promise<boolean> {
    const payload = {
      promiseState: 'complete',
    };
    try {
      const response = await this._api
        .pledge(payload, restaurantId)
        .toPromise();
      if ((response || {}).status === 1) {
        this._message.showSuccess(
          'Thanks for your confirmation. Your system has been unlocked'
        );
        return true; //return succesfull
      }
      this._message.showError((response || {}).message);
      return false;
    } catch (error) {
      this._message.showError(((error || {}).error || {}).message);
      console.error(error);
      return false;
    }
  }
}
